import api from '@/api/axios';
import ExcelJS from 'exceljs';
import type { AxiosError } from 'axios';

export interface DashboardTotalsData {
  totalUsuarios: number;
  usuariosActivos: number;
  usuariosInactivos: number;
  totalCapacitaciones: number;
  totalAsignacionesCapacitacion: number;
  totalIntentosCapacitacion: number;
  totalCertificados: number;
  totalReprobados: number;
  totalMateriales: number;
  totalPodcast: number;
  totalFaq: number;
  totalArchivos: number;
  totalCuestionarios: number;
  totalRespuestasCuestionarios: number;
  totalComunidades: number;
  totalUsuariosComunidad: number;
  totalComentariosComunidad: number;
  totalPublicaciones: number;
  totalComentariosMuro: number;
  totalLikesMuro: number;
  totalNotificaciones: number;
  usuariosQueInteractuan: number;
  usuariosConActividadCapacitacion: number;
}

export interface DashboardTotalsResponse {
  code: string;
  message: string;
  data: DashboardTotalsData;
}

interface CapacitacionReporteItem {
  id: number;
  nombreAppy: string;
  region: string | null;
  ultimaFechaRealizacion: string | null;
  idComercio: string | null;
  numIntentos: number;
  ultimosPuntosObtenidos: number;
  estatus: string;
  url: string | null;
}

export interface DateFilters {
  fechaInicio: string;
  fechaFin: string;
  idCapacitacion?: number | null;
}

export const EXPORT_NO_DATA_ERROR = 'NO_DATA';

export async function getDashboardTotals(filters: DateFilters): Promise<DashboardTotalsResponse> {
  const params = new URLSearchParams({
    fechaInicio: filters.fechaInicio,
    fechaFin: filters.fechaFin
  });
  if (filters.idCapacitacion) {
    params.append('capacitacionId', String(filters.idCapacitacion));
  }
  const response = await api.get<DashboardTotalsResponse>(
    `/appy/reporte/v1/dashboard/totales?${params.toString()}`
  );
  return response.data;
}

export async function exportCapacitaciones(filters: DateFilters): Promise<void> {
  const params = new URLSearchParams({
    size: '1000000',
    fechaInicio: filters.fechaInicio,
    fechaFin: filters.fechaFin
  });
  if (filters.idCapacitacion) {
    params.append('capacitacionId', String(filters.idCapacitacion));
  }

  let items: CapacitacionReporteItem[];

  try {
    const response = await api.get<{ data: { content: CapacitacionReporteItem[] } }>(
      `/appy/reporte/v1/capacitaciones?${params.toString()}`
    );
    items = response.data.data.content;
  } catch (err) {
    const axiosError = err as AxiosError;
    if (axiosError.response?.status === 404) {
      throw new Error(EXPORT_NO_DATA_ERROR);
    }
    throw err;
  }

  if (items.length === 0) {
    throw new Error(EXPORT_NO_DATA_ERROR);
  }

  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet('Capacitaciones');

  ws.columns = [
    { header: 'ID', key: 'id', width: 10 },
    { header: 'NOMBRE', key: 'nombreAppy', width: 25 },
    { header: 'REGION', key: 'region', width: 20 },
    { header: 'ULTIMA FECHA DE REALIZACION', key: 'ultimaFechaRealizacion', width: 30 },
    { header: 'ID COMERCIO', key: 'idComercio', width: 20 },
    { header: 'NÚM INTENTOS', key: 'numIntentos', width: 15 },
    { header: 'ULTIMOS PUNTOS OBTENIDOS', key: 'ultimosPuntosObtenidos', width: 25 },
    { header: 'ESTATUS', key: 'estatus', width: 15 },
    { header: 'URL', key: 'url', width: 30 }
  ];

  ws.getRow(1).font = { bold: true };

  items.forEach(item => {
    ws.addRow({
      id: item?.id,
      nombreAppy: item?.nombreAppy,
      region: item?.region ?? '',
      ultimaFechaRealizacion: item?.ultimaFechaRealizacion ?? '',
      idComercio: item?.idComercio ?? '',
      numIntentos: item?.numIntentos,
      ultimosPuntosObtenidos: item?.ultimosPuntosObtenidos,
      estatus: item?.estatus,
      url: item?.url ?? ''
    });
  });

  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'reporte_capacitaciones.xlsx';
  a.click();
  URL.revokeObjectURL(url);
}